import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { ConfirmationService } from 'primeng/api';
import { map, tap } from 'rxjs/operators';
import { selectEmpleosList } from '../../store/selectors/empleos.selectors';
import { ICreateEmpleo, IEmpleo, IUpdateEmpleo } from '../../interfaces/empleos.interface';
import { IAppState } from '../../store/app.state';
import { DownloadService } from 'src/app/services/download.service';
import { Table } from 'primeng/table';
import {
  CreateEmpleo, CreateEmpleoSuccess, DeleteEmpleo, DeleteEmpleoSuccess,
  GetEmpleos, UpdateEmpleo, UpdateEmpleoSuccess
} from '../../store/actions/empleos.actions';
import { DatePipe } from '@angular/common';
import { ExportExcelService } from 'src/app/services/export-excel.service';

@Component({
  selector: 'app-empleo',
  templateUrl: './empleo.component.html',
  styleUrls: ['./empleo.component.scss']
})
export class EmpleoComponent implements OnInit, OnDestroy {
  @ViewChild('dt', { static: false }) table: Table;

  empleos = this.store$.pipe(
    select(selectEmpleosList),
    map(empleos => empleos.map(empleo => {
      this.empleosExcel.push(empleo);
      return {
        ...empleo,
        tipo_jornada: empleo.tipo_jornada === '' ? 'N/A' : empleo.tipo_jornada,
        tipo_empleo: empleo.tipo_empleo === '' ? 'N/A' : empleo.tipo_empleo,
        fecha_inicio: this.datePipe.transform(empleo.fecha_inicio, 'dd/MM/yyyy'),
        fecha_postulacion: this.datePipe.transform(empleo.fecha_postulacion, 'dd/MM/yyyy'),
      }
    }))
  );

  cols: any[];
  mode = '';
  header = '';
  displayForm = false;
  displayMap = false;
  mapOptions: any;
  mapOverlays = [];
  formEmpleo: FormGroup;
  empleosExcel = [];
  dataForExcel = [];

  downloadItems = [
    { label: 'CSV', command: () => { this.downloadService.exportCSV('empleos', this.table) } },
    { label: 'PDF', command: () => { this.downloadService.exportPdf('empleos', this.table) } },
  ];

  jornada = [
    { label: 'Jornada Completa', value: 'Jornada Completa' },
    { label: 'Media Jornada', value: 'Media Jornada' }
  ];

  tipoEmpleos = [
    { label: 'Oficina', value: 'Oficina' },
    { label: 'Campo', value: 'Campo' },
    { label: 'Mixto', value: 'Mixto' }
  ];

  formSubmitSubscription = this.actions$.pipe(
    ofType(UpdateEmpleoSuccess, DeleteEmpleoSuccess, CreateEmpleoSuccess),
    tap(_ => {
      this.store$.dispatch(GetEmpleos());
      this.displayForm = false;
    })
  ).subscribe();

  constructor(
    private formBuilder: FormBuilder,
    private confirmationService: ConfirmationService,
    private store$: Store<IAppState>,
    private actions$: Actions,
    public downloadService: DownloadService,
    private datePipe: DatePipe,
    private ete: ExportExcelService
  ) {
    this.formEmpleo = this.formBuilder.group({
      _id: new FormControl(''),
      nombre: new FormControl(''),
      descripcion: new FormControl(''),
      sueldo: new FormControl(''),
      domicilio: new FormControl(''),
      fecha_inicio: new FormControl(''),
      fecha_postulacion: new FormControl(''),
      requisitos: new FormControl(''),
      perfil: new FormControl(''),
      tipo_jornada: new FormControl(''),
      tipo_empleo: new FormControl(''),
      prestaciones: new FormControl(''),
      latitud: new FormControl(''),
      longitud: new FormControl(''),
      nombre_contacto: new FormControl(''),
      correo_contacto: new FormControl(''),
      telefono_contacto: new FormControl(''),
      celular_contacto: new FormControl(''),
    });
  }

  async ngOnInit() {
    this.cols = [
      { field: 'nombre', header: 'Nombre de la empresa', exportable: true },
      { field: 'tipo_jornada', header: 'Tipo de Jornada', exportable: true },
      { field: 'tipo_empleo', header: 'Tipo de Empleo', exportable: true },
      { field: 'sueldo', header: 'Sueldo ofrecido', exportable: true },
      { field: 'ubicacion', header: 'Ubicacion', exportable: false },
      { field: 'acciones', header: 'Acciones', exportable: false }
    ];

    this.store$.dispatch(GetEmpleos())
  }

  async ngOnDestroy() {
    this.formSubmitSubscription.unsubscribe();
  }

  editarEmpleo(empleo: IEmpleo): void {
    this.mode = 'EDITAR'
    this.header = 'Editar Empleo';
    this.formEmpleo.patchValue({
      ...empleo
    });
    this.formEmpleo.enable();

    this.displayForm = true;
  }

  verEmpleo(empleo: IEmpleo): void {
    this.mode = 'DETALLE'
    this.header = 'Detalle Empleo'
    this.formEmpleo.patchValue({
      ...empleo
    });
    this.formEmpleo.disable();

    this.displayForm = true;
  }

  agregarEmpleo() {
    this.mode = 'AGREGAR'
    this.header = 'Agregar Empleo'
    this.formEmpleo.patchValue({
      _id: '',
      nombre: '',
      descripcion: '',
      sueldo: '',
      domicilio: '',
      fecha_inicio: '',
      fecha_postulacion: '',
      requisitos: '',
      perfil: '',
      tipo_jornada: '',
      tipo_empleo: '',
      prestaciones: '',
      latitud: '',
      longitud: '',
      nombre_contacto: '',
      correo_contacto: '',
      telefono_contacto: '',
      celular_contacto: '',
    })
    this.formEmpleo.enable();

    this.displayForm = true;
  }

  borrarEmpleo(empleo: IEmpleo) {
    this.confirmationService.confirm({
      header: 'Borrar Empleo',
      message: `¿Seguro que quiere borrar el empleo '${empleo.nombre}'?`,
      acceptLabel: 'Aceptar',
      rejectVisible: false,
      accept: async () => {
        this.store$.dispatch(DeleteEmpleo({ id: empleo._id }))
      }
    });
  }

  verMapa(empleo: IEmpleo) {
    this.mapOptions = {
      center: { lat: +empleo.latitud, lng: +empleo.longitud },
      zoom: 15
    }

    this.mapOverlays = [
      new google.maps.Marker({ position: { lat: +empleo.latitud, lng: +empleo.longitud }, title: empleo.nombre }),
    ];

    this.displayMap = true;
  }

  submitForm() {
    const form: IEmpleo = this.formEmpleo.getRawValue();

    if (this.mode === 'AGREGAR') {
      const payload: ICreateEmpleo = {
        ...form,
      }

      this.store$.dispatch(CreateEmpleo({ empleo: payload }));
    } else if (this.mode === 'EDITAR') {
      const payload: IUpdateEmpleo = {
        ...form,
      }

      this.store$.dispatch(UpdateEmpleo({ id: form._id, empleo: payload }));
    }

    return;
  }

  exportToExcel() {
    this.dataForExcel = [];

    const uniqueArray = this.empleosExcel.filter((value, index) => {
      const _value = JSON.stringify(value);
      return index === this.empleosExcel.findIndex(obj => {
        return JSON.stringify(obj) === _value;
      });
    });

    uniqueArray.forEach((row: any) => {
      const insert = { ...row }
      this.dataForExcel.push(Object.values(insert))
    })

    const headers = {
      id: '',
      nombre: '',
      descripcion: '',
      sueldo: '',
      domicilio: '',
      'fecha de inicio': '',
      'fecha de postulacion': '',
      requisitos: '',
      perfil: '',
      'tipo de jornada': '',
      'tipo de empleo': '',
      prestaciones: '',
      latitud: '',
      longitud: '',
      'nombre de contacto': '',
      'correo de contacto': '',
      'telefono de contacto': '',
      'celular de contacto': ''
    }

    const reportData = {
      title: 'Empleos',
      data: this.dataForExcel,
      headers: Object.keys(this.empleosExcel[0])
    }

    this.ete.exportExcel(reportData, false);
  }
}
